/**
 * OrderStructureTree — the order's fulfillment-job (sub-order) tree.
 *
 * Extracted from `OrderDetailExtra` (#1204) and, since #1804, the head of the
 * order's `fulfillment` tab — the "what is being fulfilled" layer, with the
 * admin trace below it.
 *
 * Jobs are fetched from the delivery read surface
 * (`orderAPI.getOrderTasks` → `GET /api/fulfillment-jobs?shopify_order_id=`);
 * the order-detail contract no longer embeds them (#1187, B5). Shared
 * query key with the trace panel, so the page issues one jobs fetch.
 * Three-state (loading / error / empty) so a slow or failed query never
 * silently collapses into "no sub-orders".
 *
 * Since #1920 each job also expands to the goods it carries, read from the
 * `line_items_json` the job list already returns — no second request — and the
 * tree names the order line items NO job claims. For a job with a 配送单清单
 * (ADR-0046), `line_items_json` is the whole order with each line marked
 * `_job_quantity` (`delivery/app/order-fields.ts`): the job expands only the
 * lines whose `_job_quantity` is above 0, at that quantity, so an undated
 * purchased item shows under the job whose 配送单清单 holds it (the earliest
 * job, unless the list has drifted from the order). A job whose 配送单清单 is
 * NULL (an old job) still sends a strict date-equality scope, so its undated
 * items are absent from it. The unassigned group is the lines no job expands;
 * when every job has a 配送单清单, that is the lines no list holds (the tip).
 * Listing N jobs side by side invites an operator to add them up as the
 * order; expanding without the gap would hide whatever no job claims, so the
 * two arrive together.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ListTree } from 'lucide-react';
import { Badge, EntityLink, ExternalServiceLink } from '@resource-kit/ui/react';
import { cn, enumBadgeVariant } from '@resource-kit/ui';
import { queryKeys } from '@lib/queryKeys';
import { EXTERNAL_URLS } from '@/constants/config';
import { orderAPI, type FulfillmentJob, type Order } from '@/services';
import { jobDisplayName } from '../jobDisplay';
import { jobStatusOptions } from '@features/dispatch';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { isLineItemRecord } from '@/lib/line-items';

/** A verbatim Shopify line-item object, as far as this tree reads it. */
export type VerbatimLineItem = Record<string, unknown>;

/**
 * A line item's identity across the two reads, normalised to a string.
 *
 * Both sides descend from the same stored `raw_line_items` blob, so the Shopify
 * line-item `id` matches exactly — but number-vs-string drift across JSON
 * round-trips has bitten this repo before, hence `String()`. Returns `null`
 * when the item carries no usable id.
 */
function lineItemId(item: unknown): string | null {
  if (!isLineItemRecord(item)) return null;
  const id = item.id;
  if (typeof id !== 'string' && typeof id !== 'number') return null;
  const normalised = String(id);
  return normalised === '' ? null : normalised;
}

/**
 * The line items one delivery job carries, or `null` when the payload is
 * unreadable.
 *
 * `null` is a sentinel, NOT an empty result. Collapsing an unparseable
 * `line_items_json` into `[]` would present a data fault as the statement
 * "this job carries nothing" — the one reading an operator must never be
 * handed silently. Callers render an explicit fault notice for it.
 */
export function computeJobItems(
  job: Pick<FulfillmentJob, 'line_items_json'>,
): VerbatimLineItem[] | null {
  const raw = job.line_items_json;
  if (raw == null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    // Not swallowed: the sentinel below surfaces the fault in the UI, and the
    // warn makes it greppable in the browser console.
    console.warn('[orders] job line_items_json is not valid JSON', error);
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  // A non-object entry (a job with a 配送单清单 passes raw entries through
  // verbatim) has no id and no `_job_quantity`, so the job holds nothing
  // through it: skipped, like the order side skips it in `computeGap`.
  return (parsed as unknown[])
    .filter(isLineItemRecord)
    .filter((entry) => typeof entry._job_quantity !== 'number' || entry._job_quantity > 0);
}

/**
 * Order line items that no delivery job claims.
 *
 * Identity is the Shopify line-item `id` and nothing else. Job membership
 * comes from the backend: the `_job_quantity` mark for a job with a
 * 配送单清单, or the strict date rule for a job whose 配送单清单 is NULL.
 * Recomputing either here would invent another scope. Matching by product
 * name is likewise out — a rename split rows once already (#1623).
 *
 * An item carrying no usable id cannot be matched, so it counts as unassigned:
 * a visible data-quality signal beats a quietly smaller gap.
 *
 * Returns `null` when ANY job's items are unreadable — the difference is then
 * unknowable, and an admitted blind spot beats a confidently wrong list.
 */
export function computeGap(
  orderLineItems: readonly unknown[],
  jobs: readonly Pick<FulfillmentJob, 'line_items_json'>[],
): VerbatimLineItem[] | null {
  const assigned = new Set<string>();
  for (const job of jobs) {
    const items = computeJobItems(job);
    if (items === null) return null;
    for (const item of items) {
      const id = lineItemId(item);
      if (id !== null) assigned.add(id);
    }
  }
  return orderLineItems.filter((item): item is VerbatimLineItem => {
    if (!isLineItemRecord(item)) return false;
    const id = lineItemId(item);
    return id === null || !assigned.has(id);
  });
}

export function OrderStructureTree({ order }: { order: Order }) {
  const { t } = useTranslation();
  const [expandedJobIds, setExpandedJobIds] = useState<ReadonlySet<string>>(new Set());

  const toggleJob = (jobId: string) => {
    setExpandedJobIds((previous) => {
      const next = new Set(previous);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const jobsQuery = useQuery({
    queryKey: queryKeys.orders.jobs(order.shopify_id),
    queryFn: () => orderAPI.getOrderTasks(order.shopify_id),
    staleTime: 60_000,
  });
  const jobs = jobsQuery.data?.items ?? [];

  // `Order` is the generated `OrderSummary`, whose `line_items` is a verbatim
  // Shopify array — the same blob the delivery read lists for a job with a
  // 配送单清单 and filters by date for a job whose 配送单清单 is NULL. The
  // runtime guard is for a malformed sync, not for a contract gap.
  const orderLineItems: readonly unknown[] = Array.isArray(order.line_items)
    ? order.line_items
    : [];
  const gap = computeGap(orderLineItems, jobs);

  return (
    <section className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <ListTree className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">{t('order.structure.title')}</h3>
      </div>
      {jobsQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">{t('order.structure.loading')}</div>
      ) : jobsQuery.isError ? (
        <div className="text-xs text-destructive">
          {t('order.structure.errorLoad', { detail: jobsQuery.error.message })}
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-xs text-muted-foreground">{t('order.structure.noJobs')}</div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.id} className="border rounded-md p-3 bg-surface-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <EntityLink to={`/dispatch/jobs/${job.id}`} className="font-mono text-sm">
                    {jobDisplayName(job)}
                  </EntityLink>
                  <JobStatusBadge status={job.status} />
                  {job.onfleet_task_id && (
                    <ExternalServiceLink
                      href={EXTERNAL_URLS.onfleetTask(job.onfleet_task_id)}
                      label="Onfleet"
                      ariaLabel={t('ui.openInOnfleet')}
                    />
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {job.shipping_date
                    ? t('order.structure.deliveryDate', { date: job.shipping_date })
                    : t('order.structure.flexibleDate')}
                </div>
              </div>
              {job.presumed_coverage && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {t('order.structure.presumedCoverage', {
                    order: job.presumed_coverage.covering_order_name,
                    task: job.presumed_coverage.covering_task_short_id ?? '—',
                  })}
                </div>
              )}
              <JobLineItems
                job={job}
                expanded={expandedJobIds.has(job.id)}
                onToggle={() => toggleJob(job.id)}
              />
            </div>
          ))}
          <UnassignedItems gap={gap} />
        </div>
      )}
    </section>
  );
}

/** One job's goods, behind a local toggle — the data is already in hand. */
function JobLineItems({
  job,
  expanded,
  onToggle,
}: {
  job: FulfillmentJob;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const items = computeJobItems(job);

  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
      >
        <ChevronDown
          className={cn('w-3 h-3 transition-transform shrink-0', expanded && 'rotate-180')}
        />
        {t('order.structure.toggleItems')}
      </button>
      {expanded &&
        (items === null ? (
          <div className="mt-1 text-xs text-destructive">
            {t('order.structure.itemsUnavailable')}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-1 text-xs text-muted-foreground">
            {t('order.structure.itemsEmpty')}
          </div>
        ) : (
          <ul data-testid={`order-structure-job-items-${job.id}`} className="mt-1 space-y-0.5">
            {items.map((item, index) => (
              <LineItemRow key={lineItemId(item) ?? `index-${index}`} item={item} />
            ))}
          </ul>
        ))}
    </div>
  );
}

/**
 * The order's own line items that no job carries. Rendered only when there is
 * something to say: an empty group would be noise, and the `null` sentinel gets
 * an explicit "cannot be computed" rather than a wrong difference.
 */
function UnassignedItems({ gap }: { gap: VerbatimLineItem[] | null }) {
  const { t } = useTranslation();

  if (gap === null) {
    return (
      <div className="border border-dashed rounded-md p-3 text-xs text-destructive">
        {t('order.structure.gapUnavailable')}
      </div>
    );
  }
  if (gap.length === 0) return null;

  return (
    <div className="border border-dashed rounded-md p-3">
      <div className="text-xs font-medium text-foreground">
        {t('order.structure.unassignedItems')}
      </div>
      <ul data-testid="order-structure-gap" className="mt-1 space-y-0.5">
        {gap.map((item, index) => (
          <LineItemRow key={lineItemId(item) ?? `index-${index}`} item={item} />
        ))}
      </ul>
    </div>
  );
}

/**
 * Title and quantity, rendered as separate nodes rather than one interpolated
 * sentence so a product name containing markup-ish characters stays literal
 * (the catalog runs with `escapeValue: false`).
 */
function LineItemRow({ item }: { item: VerbatimLineItem }) {
  const { t } = useTranslation();
  const rawTitle = item.title ?? item.name;
  const title =
    typeof rawTitle === 'string' && rawTitle.trim() !== ''
      ? rawTitle
      : t('order.structure.itemUntitled');
  const quantity =
    typeof item._job_quantity === 'number'
      ? item._job_quantity
      : typeof item.quantity === 'number'
        ? item.quantity
        : null;

  return (
    <li className="text-xs text-muted-foreground">
      <span>{title}</span>
      {quantity !== null && (
        <span className="ml-1">{t('order.structure.itemQuantity', { quantity })}</span>
      )}
    </li>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  // Same SOT catalog the jobs list renders from: translated label,
  // consistent variant, loud [unknown:*] marker on wire-form drift.
  const option = jobStatusOptions.find((o) => o.value === status);
  return (
    <Badge variant={enumBadgeVariant(option?.color)}>
      {lookupEnumLabelOrWarn(status, jobStatusOptions, 'order sub-job status')}
    </Badge>
  );
}
